import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import tw from 'tailwind.macro';
import { CloseIcon } from '../../images/icons/close';
import { colors } from '../../styles/constants';
import { localizePath, URL_DEFAULT_LOCALE } from '../../utils/i18nUrls';
import {
  getSuggestedLocale,
  setPreferredLocale,
} from '../../utils/localePreference';
import { Icon12 } from '../Icon';
import { translationBannerMessages } from './messages';

const Container = styled.div`
  ${tw`flex items-center justify-center flex-wrap
    px-medium py-small
    bg-toast-warning-light border-toast-warning border-b border-solid
    text-p-small-m leading-p-small-m`}
  gap: var(--size-small);
`;

const SwitchLink = styled(Link)`
  ${tw`text-text underline text-p-small-m leading-p-small-m font-semibold`}
`;

const DismissButton = styled.button`
  ${tw`bg-transparent border-none cursor-pointer p-0 px-small`}
`;

/**
 * @description banner shown on french (unprefixed) URLs when the browser
 * language, or an earlier explicit choice, points at a translated version of
 * the site. It only suggests: the page stays french until the visitor asks
 * for the translation, and the dismissal is remembered so nobody is asked
 * twice. Written in the suggested language, the one the visitor reads best,
 * read straight from the per-locale message strings (the format every
 * translation bundle is built from, see translations/util.js).
 *
 * Mounted globally (see containers/App), but getSuggestedLocale decides where
 * it actually shows up: the landing pages only — never in the funnel, where
 * changing URL mid-booking is at best a distraction — never for crawlers, so
 * prerendered snapshots stay single-language, and never inside the mobile
 * app's WebView.
 */
const TranslationBanner = () => {
  const { hash, pathname, search } = useLocation();
  const [isDismissed, setIsDismissed] = useState(false);
  const suggestedLocale = isDismissed ? null : getSuggestedLocale(pathname);

  if (!suggestedLocale) return null;

  const t = (message: Record<string, string>) => message[suggestedLocale];
  const targetUrl = localizePath(pathname, suggestedLocale) + search + hash;

  const onDismiss = () => {
    setPreferredLocale(URL_DEFAULT_LOCALE);
    setIsDismissed(true);
  };

  return (
    <Container lang={suggestedLocale} role="status">
      <span>{t(translationBannerMessages.availableIn)}</span>
      <SwitchLink
        onClick={() => setPreferredLocale(suggestedLocale)}
        to={targetUrl}
      >
        {t(translationBannerMessages.switchTo)}
      </SwitchLink>
      <DismissButton
        aria-label={t(translationBannerMessages.dismiss)}
        onClick={onDismiss}
        type="button"
      >
        <CloseIcon color={colors.text} customStyle={Icon12} />
      </DismissButton>
    </Container>
  );
};

export default TranslationBanner;
